/**
 * One readiness measure for a billing year, used by every screen (Dashboard, Objekte, Objekt-Übersicht, Hausakte,
 * Abrechnungen, Checkliste) so the landlord always reads the same number with the same words:
 * "3 von 12 Kostenarten fehlen noch · 2 Belege prüfen". Input: the cost rows of the year file
 * (GET /properties/:id/year-file, `costs[].status`), for periods over two years the merged rows.
 */
export type BillingReadiness = {
  /** cost types that count for the year (without "not applicable") */
  total: number;
  /** cost types with all receipts confirmed */
  done: number;
  /** cost types without a receipt yet */
  missing: number;
  /** cost types whose receipt waits for review */
  review: number;
  /** cost types expected later in the running year (not due yet) */
  later: number;
  /** meter readings missing at a cut-off day of the period (insights `reading_missing`, 0 when not known) */
  readings: number;
  /** done / total (0 when nothing is expected) */
  share: number;
  /** nothing to do right now: no missing cost type, no receipt to review, no missing meter reading */
  complete: boolean;
};

/**
 * `readingsMissing`: meter readings the period still needs (insights items of kind `reading_missing`); they hold the
 * statement up like a missing receipt, so they are part of the same measure and sentence.
 */
export function billingReadiness(
  rows: readonly { status: string }[],
  opts: { readingsMissing?: number } = {},
): BillingReadiness {
  const relevant = rows.filter((r) => r.status !== 'not_applicable');
  const count = (s: string) => relevant.filter((r) => r.status === s).length;
  const done = count('complete');
  const missing = count('missing');
  const review = count('review');
  const later = count('expected_later');
  const readings = Math.max(0, opts.readingsMissing ?? 0);
  return {
    total: relevant.length,
    done,
    missing,
    review,
    later,
    readings,
    share: relevant.length ? done / relevant.length : 0,
    complete: missing === 0 && review === 0 && readings === 0,
  };
}

type MonthRow = { category: string; months: readonly string[]; status: string };

/**
 * Cost rows of a billing period from the calendar year files it touches (a period from 01.07.2025 to 30.06.2026 needs
 * the year files 2025 and 2026): per cost type only the months inside the period count, and the status follows the
 * same rule as the server's year file (review, then missing, then expected later; all months n/a = not applicable).
 * A calendar-year period returns the rows of its one year file unchanged.
 */
export function periodCostRows<R extends MonthRow>(
  files: readonly { year: number; costs: readonly R[] }[],
  from: string,
  to: string,
): R[] {
  if (files.length === 1 && from.endsWith('-01-01') && to.endsWith('-12-31') && from.slice(0, 4) === to.slice(0, 4))
    return [...files[0]!.costs];
  const months = new Map<string, { row: R; months: string[] }>();
  for (const f of files) {
    for (const c of f.costs) {
      const inside = c.months.filter((_, m) => {
        const key = `${f.year}-${String(m + 1).padStart(2, '0')}`;
        return key >= from.slice(0, 7) && key <= to.slice(0, 7);
      });
      const cur = months.get(c.category);
      if (cur) cur.months.push(...inside);
      else months.set(c.category, { row: c, months: [...inside] });
    }
  }
  return [...months.values()].map(({ row, months: m }) => {
    const status = m.includes('review')
      ? 'review'
      : m.includes('missing')
        ? 'missing'
        : m.includes('expected')
          ? 'expected_later'
          : m.every((x) => x === 'na')
            ? 'not_applicable'
            : 'complete';
    return { ...row, months: m, status };
  });
}

/** Text parts of the measure as i18n keys with params (the apps translate; `count` drives the plural form). */
export function readinessParts(r: BillingReadiness): { key: string; params: Record<string, number> }[] {
  const parts: { key: string; params: Record<string, number> }[] = [];
  if (r.missing)
    parts.push({ key: 'common:readiness.missing', params: { count: r.missing, total: r.total } });
  if (r.review) parts.push({ key: 'common:readiness.review', params: { count: r.review } });
  if (r.readings) parts.push({ key: 'common:readiness.readings', params: { count: r.readings } });
  if (!parts.length) {
    if (!r.total) parts.push({ key: 'common:readiness.nothingExpected', params: {} });
    else if (r.later)
      parts.push({ key: 'common:readiness.later', params: { count: r.later, total: r.total } });
    else parts.push({ key: 'common:readiness.complete', params: { count: r.total } });
  }
  return parts;
}

/**
 * The same measure for a small tile (MobileHaus "Abrechnung 2025"): a short value and the rest as a secondary line,
 * e.g. "3 fehlen" + "von 12 Kostenarten · 1 Beleg prüfen". Same numbers and words as `readinessParts`, split for space.
 */
export function readinessShort(r: BillingReadiness): {
  value: { key: string; params: Record<string, number> };
  sub: { key: string; params: Record<string, number> }[];
} {
  const review = r.review ? [{ key: 'common:readiness.review', params: { count: r.review } }] : [];
  if (r.missing)
    return {
      value: { key: 'common:readiness.short.missing', params: { count: r.missing } },
      sub: [{ key: 'common:readiness.short.ofTotal', params: { count: r.total } }, ...review],
    };
  if (r.review)
    return {
      value: { key: 'common:readiness.short.review', params: { count: r.review } },
      sub: [{ key: 'common:readiness.short.reviewSub', params: { count: r.review } }],
    };
  if (!r.total)
    return {
      value: { key: 'common:readiness.short.none', params: {} },
      sub: [{ key: 'common:readiness.nothingExpected', params: {} }],
    };
  if (r.later)
    return {
      value: { key: 'common:readiness.short.soFar', params: {} },
      sub: [{ key: 'common:readiness.short.later', params: { count: r.later } }],
    };
  return {
    value: { key: 'common:readiness.short.complete', params: {} },
    sub: [{ key: 'common:readiness.short.ofTotalDone', params: { count: r.total } }],
  };
}

type PeriodLike = { propertyId: string; from: string; to: string; status: string };
const OPEN_PERIOD = ['collecting', 'ready', 'draft', 'final'];

/**
 * The billing period of a property that needs work now: the oldest ended one that is not sent yet (after the last sent
 * or closed one), else the running one, else the last started one (or the first future one). Shared by the web
 * dashboard and the server's dashboard inbox (GET /dashboard/inbox) so both pick the same period.
 */
export function actionablePeriod<P extends PeriodLike>(periods: readonly P[], propertyId: string, day: string): P | undefined {
  const list = periods.filter((p) => String(p.propertyId) === propertyId).sort((a, b) => a.from.localeCompare(b.from));
  let done = -1;
  list.forEach((p, i) => {
    if (p.status === 'sent' || p.status === 'closed') done = i;
  });
  const due = list.slice(done + 1).find((p) => p.to < day && OPEN_PERIOD.includes(p.status));
  return due ?? list.filter((p) => p.from <= day).pop() ?? list[0];
}
